// =============================================================================
// GUARD — Este archivo se importa desde CLIENTE (apps/dashboard) Y SERVER
// (apps/worker-api). Es la fuente de verdad UNICA del catalogo de permisos y de
// los roles base de PrintFlow (ADR 0007).
//
// Reglas inviolables:
//   1. Solo imports isomorficos de `better-auth/plugins/access`. Ese modulo
//      exporta un factory puro (`createAccessControl`): sin side-effects, sin
//      conexiones, sin filesystem.
//   2. PROHIBIDO importar `drizzle-orm`, `pg`, la schema, `node:*` o cualquier
//      adapter server-only. Rompe el bundle del Worker (cold-start) y el build
//      del dashboard (Vite no resuelve `node:*`).
//   3. NUNCA reordenar acciones dentro de un recurso ni renombrar roles sin
//      auditoria de seguridad: un reorder silencioso es un cambio semantico
//      oculto, la categoria mas dificil de detectar en review.
//   4. Agregar un permiso se hace SOLO aca. Worker y dashboard lo reciben en el
//      mismo commit.
//
// ALCANCE (ADR 0015): los permisos terminados en `_all` declaran que el rol ve o
// edita TODAS las filas y no solo las propias. **Ausencia = solo lo propio.**
// imprenta resolvia esto con filtros escritos en el endpoint, invisibles en la
// matriz de permisos; aca es un casillero que el Manager puede tildar.
//
// PROCEDENCIA: los 55 permisos restantes y la matriz rol->permisos salen 1:1 de imprenta
// (`backend/seed_permissions_and_roles.py`, derivado de
// `specs/spec_implemented.md` §3.2). Cualquier divergencia es un bug de
// migracion, no una mejora.
// =============================================================================

import { createAccessControl } from "better-auth/plugins/access";

/**
 * Catalogo CERRADO de permisos: `recurso -> acciones`.
 *
 * El codigo plano de imprenta se reconstruye como `${recurso}.${accion}` para
 * los 55, sin excepciones — por eso hay acciones con punto adentro
 * (`roles.permisos.read`, `sistema.config.read`). Se conservan tal cual: son la
 * clave con la que el sistema viejo persiste permisos, y renombrarlas seria
 * romper la equivalencia que hace verificable la migracion.
 *
 * Las verbas NO se cierran a CRUD. El dominio de imprenta tiene acciones
 * propias (`aprobar`, `convertir`, `asignar`, `arte`, `horas`, `estados`,
 * `import`, `bulk_update`, `flexible_transition`, `imprimir_sin_aprobacion`) y forzarlas a
 * {create,read,update,delete} perderia semantica.
 */
export const pfStatement = {
  presupuestos: ["create", "read", "update", "update_all", "delete", "aprobar", "convertir"],
  clientes: ["create", "read", "update", "delete", "import"],
  produccion: [
    "create",
    "read",
    "read_all",
    "update",
    "update_all",
    "delete",
    "asignar",
    "arte",
    "horas",
    "estados",
    "precios",
    "flexible_transition",
    // Ver ADR 0098 (A6): imprimir sin la aprobacion del cliente.
    "imprimir_sin_aprobacion",
  ],
  impresoras: ["create", "read", "update", "delete"],
  usuarios: ["create", "read", "update", "delete"],
  roles: ["create", "read", "update", "delete", "permisos.read", "permisos.update"],
  catalogos: ["create", "read", "update", "delete"],
  productos: ["create", "read", "update", "delete", "import"],
  precios: ["create", "read", "update", "delete", "bulk_update"],
  auditoria: ["read"],
  reportes: ["negocio"],
  // Facturacion: NO viene del catalogo de `imprenta` (ADR 0026). Alla el
  // endpoint consultaba `facturacion:read` sin que existiera en ningun
  // catalogo, asi que respondia 403 a todos los roles. `revertir`: ADR 0099.
  facturacion: ["read", "write", "revertir"],
  sistema: ["config.read", "config.update", "metricas", "logs"],
  // ─── INTERNOS del plugin, no del producto (ADR 0018) ─────────────────────
  //
  // Better Auth chequea SU propio statement antes de dejar hacer nada: `ac`
  // para el CRUD de roles dinamicos, `member` para asignar roles y quitar
  // gente, `invitation` para invitar. Si no los declaramos, esas operaciones
  // dan 403 para TODOS, incluido el Administrador — la feature no existe.
  //
  // Son el nombre privado que la libreria le da a autoridades que el catalogo
  // de imprenta ya expresa, asi que se otorgan EN ESPEJO: `ac` sigue a
  // `roles.*`, `member` e `invitation` siguen a `usuarios.*`.
  //
  // Quedan FUERA de `PERMISSIONS`: no se muestran en el editor y no se pueden
  // asignar a un rol dinamico. Administrar roles y usuarios es de los roles
  // base, que son inmutables.
  //
  // NO se declaran `organization` ni `team` a proposito: la organizacion es
  // unica y no se actualiza ni se borra (ADR 0014), y el plugin de teams esta
  // apagado. Sin declararlos, nadie puede hacerlo ni por error.
  ac: ["create", "read", "update", "delete"],
  member: ["create", "update", "delete"],
  invitation: ["create", "cancel"],
} as const;

/** Recursos del plugin, separados del catalogo de producto. */
export const RECURSOS_INTERNOS: ReadonlySet<string> = new Set(["ac", "member", "invitation"]);

/** @deprecated Usar `RECURSOS_INTERNOS`. Se conserva por compatibilidad de nombre. */
export const RECURSO_INTERNO = "ac" satisfies keyof typeof pfStatement;

export const pfAc = createAccessControl(pfStatement);

export type Recurso = keyof typeof pfStatement;

/**
 * Forma canonica de un mapa de permisos: `recurso -> acciones`.
 *
 * Es el shape que devuelve `role.statements` de Better Auth, el que se persiste
 * como JSON en la columna `permission` de los roles dinamicos, y el que viaja
 * al dashboard. Un solo shape en todo el sistema.
 */
export type PermissionsMap = Record<string, string[]>;

// ---------------------------------------------------------------------------
// Roles BASE — inmutables, definidos en codigo (ADR 0007).
//
// Los 6 roles de sistema de imprenta. No se pueden editar ni borrar: sus
// permisos son estos y punto. Los roles que el Manager crea en runtime
// (Disenador, Cortador, Atencion al Cliente) son DINAMICOS y viven en la tabla
// `roles`; se resuelven despues de estos.
//
// Consecuencia deliberada: a diferencia de imprenta, los permisos de los 6 NO
// se editan desde la consola. A cambio, es estructuralmente imposible que un
// Administrador se auto-bloquee revocandose `roles.permisos.update`.
// ---------------------------------------------------------------------------

/** Control total, incluida configuracion de sistema y logs tecnicos. */
export const administrador = pfAc.newRole({
  presupuestos: ["create", "read", "update", "update_all", "delete", "aprobar", "convertir"],
  clientes: ["create", "read", "update", "delete", "import"],
  produccion: [
    "create",
    "read",
    "read_all",
    "update",
    "update_all",
    "delete",
    "asignar",
    "arte",
    "horas",
    "estados",
    "precios",
    "flexible_transition",
    // Ver ADR 0098 (A6): imprimir sin la aprobacion del cliente.
    "imprimir_sin_aprobacion",
  ],
  impresoras: ["create", "read", "update", "delete"],
  usuarios: ["create", "read", "update", "delete"],
  roles: ["create", "read", "update", "delete", "permisos.read", "permisos.update"],
  catalogos: ["create", "read", "update", "delete"],
  productos: ["create", "read", "update", "delete", "import"],
  precios: ["create", "read", "update", "delete", "bulk_update"],
  auditoria: ["read"],
  reportes: ["negocio"],
  // Facturacion: Administrador y Manager (ADR 0026, enmendado por 0099 C1).
  facturacion: ["read", "write", "revertir"],
  sistema: ["config.read", "config.update", "metricas", "logs"],
  ac: ["create", "read", "update", "delete"],
  member: ["create", "update", "delete"],
  invitation: ["create", "cancel"],
});

/**
 * Control total del NEGOCIO. La diferencia con Administrador es exactamente el
 * bloque `sistema` — sin configuracion, sin metricas tecnicas, sin logs.
 */
export const manager = pfAc.newRole({
  presupuestos: ["create", "read", "update", "update_all", "delete", "aprobar", "convertir"],
  clientes: ["create", "read", "update", "delete", "import"],
  produccion: [
    "create",
    "read",
    "read_all",
    "update",
    "update_all",
    "delete",
    "asignar",
    "arte",
    "horas",
    "estados",
    "precios",
    "flexible_transition",
    // Ver ADR 0098 (A6): imprimir sin la aprobacion del cliente.
    "imprimir_sin_aprobacion",
  ],
  impresoras: ["create", "read", "update", "delete"],
  usuarios: ["create", "read", "update", "delete"],
  roles: ["create", "read", "update", "delete", "permisos.read", "permisos.update"],
  catalogos: ["create", "read", "update", "delete"],
  productos: ["create", "read", "update", "delete", "import"],
  precios: ["create", "read", "update", "delete", "bulk_update"],
  auditoria: ["read"],
  reportes: ["negocio"],
  // ADR 0099 C1: el Manager tambien maneja la cola de facturacion.
  facturacion: ["read", "write", "revertir"],
  ac: ["create", "read", "update", "delete"],
  member: ["create", "update", "delete"],
  invitation: ["create", "cancel"],
});

/** Area comercial completa; produccion en solo-lectura. */
export const leadVentas = pfAc.newRole({
  presupuestos: ["create", "read", "update", "update_all", "delete", "aprobar", "convertir"],
  clientes: ["create", "read", "update", "delete"],
  produccion: ["read", "read_all"],
  impresoras: ["read"],
  catalogos: ["read"],
  productos: ["read"],
  precios: ["read"],
  reportes: ["negocio"],
});

/**
 * Area productiva completa; comercial en solo-lectura.
 *
 * `precios` sin `bulk_update`: la actualizacion masiva por porcentaje queda
 * reservada a Manager y Administrador. Es asi en imprenta y se conserva.
 */
export const leadProduccion = pfAc.newRole({
  presupuestos: ["read"],
  clientes: ["read"],
  produccion: [
    "create",
    "read",
    "read_all",
    "update",
    "update_all",
    "delete",
    "asignar",
    "arte",
    "horas",
    "estados",
    "precios",
    // Sin los permisos de salto: ver ADR 0098 (A3).
  ],
  impresoras: ["create", "read", "update", "delete"],
  catalogos: ["read"],
  productos: ["create", "read", "update", "delete", "import"],
  precios: ["create", "read", "update", "delete"],
  reportes: ["negocio"],
});

/** Empleado comercial: crea y edita presupuestos; NO aprueba ni borra. */
export const ventas = pfAc.newRole({
  presupuestos: ["create", "read", "update", "convertir"],
  clientes: ["create", "read", "update"],
  produccion: ["read", "read_all"],
  impresoras: ["read"],
  catalogos: ["read"],
  productos: ["read"],
  precios: ["read"],
});

/**
 * Empleado productivo: opera sus OTs.
 *
 * `produccion.precios` esta deliberadamente AUSENTE — un Operario no ve
 * precios. Es la razon por la que el tablero en vivo no puede ser un broadcast
 * plano (ver ADR 0006).
 */
export const operario = pfAc.newRole({
  clientes: ["read"],
  produccion: ["read", "update", "arte", "horas", "estados"],
  impresoras: ["read"],
  catalogos: ["read"],
  productos: ["read"],
});

/**
 * Roles base, por nombre. La resolucion en runtime consulta este mapa PRIMERO y
 * la tabla `roles` despues (`resolveRolePermissions` en el worker), de modo que
 * una fila en la DB no pueda shadowear ni secuestrar un rol base.
 */
export const PF_ROLES = {
  Administrador: administrador,
  Manager: manager,
  "Lead Ventas": leadVentas,
  "Lead Produccion": leadProduccion,
  Ventas: ventas,
  Operario: operario,
} as const;

export type RolBaseNombre = keyof typeof PF_ROLES;

/** Nombres de los roles base — para rechazar colisiones al crear dinamicos. */
export const PF_ROLE_NAMES: readonly string[] = Object.keys(PF_ROLES);

/**
 * `true` si el nombre corresponde a un rol base (inmutable).
 *
 * Comparacion EXACTA: es la que usa la resolucion en runtime, donde
 * `member.role` guarda el nombre tal cual y "Manager" es "Manager".
 */
export function isRolBase(nombre: string): nombre is RolBaseNombre {
  return Object.hasOwn(PF_ROLES, nombre);
}

const NOMBRES_BASE_NORMALIZADOS: ReadonlySet<string> = new Set(
  PF_ROLE_NAMES.map((n) => n.toLowerCase()),
);

/**
 * `true` si el nombre CHOCA con un rol base, ignorando mayusculas.
 *
 * Existe por un detalle de Better Auth: al crear un rol dinamico normaliza el
 * nombre a minusculas y **despues** lo compara contra los nombres base tal como
 * se los pasamos ("Manager"). Como "manager" !== "Manager", el plugin deja
 * crearlo, y la consola termina mostrando dos roles que se leen igual — con
 * permisos distintos. No es escalada (la resolucion compara exacto y el
 * dinamico nunca puede exceder a su creador), pero si es una trampa para quien
 * asigna roles.
 */
export function colisionaConRolBase(nombre: string): boolean {
  return NOMBRES_BASE_NORMALIZADOS.has(nombre.trim().toLowerCase());
}

/**
 * Permisos de cada rol base como `PermissionsMap` plano. Es lo que consume la
 * resolucion en runtime y lo que se compara en los chequeos anti-escalada.
 */
export const PF_ROLE_PERMISSIONS: Readonly<Record<string, PermissionsMap>> = Object.fromEntries(
  Object.entries(PF_ROLES).map(([nombre, rol]) => [
    nombre,
    rol.statements as unknown as PermissionsMap,
  ]),
);

// ---------------------------------------------------------------------------
// Metadata del catalogo — solo para la UI.
//
// El enforcement usa `pfStatement`; esto existe para que la consola de permisos
// pueda mostrar etiquetas legibles y agrupar por tipo de accion. `tipo` conserva
// el `AccionEnum` de imprenta (CREATE/READ/UPDATE/DELETE/SPECIAL), que ahi era
// una columna aparte del codigo.
// ---------------------------------------------------------------------------

export type TipoAccion = "CREATE" | "READ" | "UPDATE" | "DELETE" | "SPECIAL";

export interface PermisoMeta {
  readonly tipo: TipoAccion;
  readonly descripcion: string;
}

// biome-ignore format: tabla de catalogo, una fila por permiso
const META: Record<string, PermisoMeta> = {
  "presupuestos.create": { tipo: "CREATE", descripcion: "Crear presupuestos" },
  "presupuestos.read": { tipo: "READ", descripcion: "Ver presupuestos" },
  "presupuestos.update": { tipo: "UPDATE", descripcion: "Editar presupuestos" },
  "presupuestos.update_all": { tipo: "UPDATE", descripcion: "Editar TODOS los presupuestos, no solo los propios" },
  "presupuestos.delete": { tipo: "DELETE", descripcion: "Eliminar presupuestos" },
  "presupuestos.aprobar": { tipo: "SPECIAL", descripcion: "Aprobar/rechazar presupuestos" },
  "presupuestos.convertir": { tipo: "SPECIAL", descripcion: "Convertir presupuesto a produccion" },

  "clientes.create": { tipo: "CREATE", descripcion: "Crear clientes" },
  "clientes.read": { tipo: "READ", descripcion: "Ver clientes" },
  "clientes.update": { tipo: "UPDATE", descripcion: "Editar clientes" },
  "clientes.delete": { tipo: "DELETE", descripcion: "Eliminar clientes" },
  "clientes.import": { tipo: "SPECIAL", descripcion: "Importar clientes desde Excel" },

  "produccion.create": { tipo: "CREATE", descripcion: "Crear ordenes de trabajo" },
  "produccion.read": { tipo: "READ", descripcion: "Ver ordenes de trabajo" },
  "produccion.update": { tipo: "UPDATE", descripcion: "Editar ordenes de trabajo" },
  "produccion.read_all": { tipo: "READ", descripcion: "Ver TODAS las OT, no solo las asignadas" },
  "produccion.update_all": { tipo: "UPDATE", descripcion: "Editar TODAS las OT, no solo las asignadas" },
  "produccion.delete": { tipo: "DELETE", descripcion: "Eliminar ordenes de trabajo" },
  "produccion.asignar": { tipo: "SPECIAL", descripcion: "Asignar impresora y responsable" },
  "produccion.arte": { tipo: "SPECIAL", descripcion: "Gestionar arte y diseno" },
  "produccion.horas": { tipo: "SPECIAL", descripcion: "Registrar horas de trabajo" },
  "produccion.estados": { tipo: "SPECIAL", descripcion: "Cambiar estados de OT" },
  "produccion.precios": { tipo: "READ", descripcion: "Ver precios en produccion" },
  "produccion.flexible_transition": { tipo: "SPECIAL", descripcion: "Forzar el estado de una OT salteando el flujo, el arte y la asignacion, con motivo" },
  "produccion.imprimir_sin_aprobacion": { tipo: "SPECIAL", descripcion: "Pasar una OT a impresion sin la aprobacion del cliente" },

  "impresoras.create": { tipo: "CREATE", descripcion: "Crear impresoras" },
  "impresoras.read": { tipo: "READ", descripcion: "Ver impresoras" },
  "impresoras.update": { tipo: "UPDATE", descripcion: "Editar impresoras" },
  "impresoras.delete": { tipo: "DELETE", descripcion: "Eliminar impresoras" },

  "usuarios.create": { tipo: "CREATE", descripcion: "Crear usuarios (invitaciones)" },
  "usuarios.read": { tipo: "READ", descripcion: "Ver usuarios" },
  "usuarios.update": { tipo: "UPDATE", descripcion: "Editar usuarios" },
  "usuarios.delete": { tipo: "DELETE", descripcion: "Desactivar usuarios" },

  "roles.create": { tipo: "CREATE", descripcion: "Crear roles" },
  "roles.read": { tipo: "READ", descripcion: "Ver roles" },
  "roles.update": { tipo: "UPDATE", descripcion: "Editar roles" },
  "roles.delete": { tipo: "DELETE", descripcion: "Eliminar roles" },
  "roles.permisos.read": { tipo: "READ", descripcion: "Ver permisos de roles" },
  "roles.permisos.update": { tipo: "UPDATE", descripcion: "Modificar permisos de roles" },

  "catalogos.create": { tipo: "CREATE", descripcion: "Crear catalogos" },
  "catalogos.read": { tipo: "READ", descripcion: "Ver catalogos" },
  "catalogos.update": { tipo: "UPDATE", descripcion: "Editar catalogos" },
  "catalogos.delete": { tipo: "DELETE", descripcion: "Eliminar catalogos" },

  "productos.create": { tipo: "CREATE", descripcion: "Crear productos" },
  "productos.read": { tipo: "READ", descripcion: "Ver productos" },
  "productos.update": { tipo: "UPDATE", descripcion: "Editar productos" },
  "productos.delete": { tipo: "DELETE", descripcion: "Eliminar productos" },
  "productos.import": { tipo: "SPECIAL", descripcion: "Importar productos desde Excel" },

  "precios.create": { tipo: "CREATE", descripcion: "Crear precios de impresion" },
  "precios.read": { tipo: "READ", descripcion: "Ver precios de impresion" },
  "precios.update": { tipo: "UPDATE", descripcion: "Editar precios de impresion" },
  "precios.delete": { tipo: "DELETE", descripcion: "Eliminar precios de impresion" },
  "precios.bulk_update": { tipo: "SPECIAL", descripcion: "Actualizar precios masivamente por porcentaje" },

  "auditoria.read": { tipo: "READ", descripcion: "Ver auditoria" },
  "reportes.negocio": { tipo: "READ", descripcion: "Ver reportes de negocio" },
  "facturacion.read": { tipo: "READ", descripcion: "Ver la cola de facturacion pendiente" },
  "facturacion.write": {
    tipo: "UPDATE",
    descripcion: "Tomar y cerrar pendientes de facturacion",
  },
  "facturacion.revertir": {
    tipo: "SPECIAL",
    descripcion: "Revertir una facturacion con su nota de credito",
  },

  "sistema.config.read": { tipo: "READ", descripcion: "Ver configuracion del sistema" },
  "sistema.config.update": { tipo: "UPDATE", descripcion: "Modificar configuracion del sistema" },
  "sistema.metricas": { tipo: "READ", descripcion: "Ver metricas tecnicas" },
  "sistema.logs": { tipo: "READ", descripcion: "Ver logs tecnicos" },
};

export const PERMISO_META: Readonly<Record<string, PermisoMeta>> = META;

/**
 * Catalogo aplanado: los 55 permisos como `{ codigo, recurso, accion, tipo,
 * descripcion }`. Derivado de `pfStatement`, no escrito a mano — asi no puede
 * desincronizarse del catalogo que enforza.
 */
export const PERMISSIONS: readonly {
  codigo: string;
  recurso: Recurso;
  accion: string;
  tipo: TipoAccion;
  descripcion: string;
}[] = Object.entries(pfStatement)
  // Los internos del plugin no se listan, no se editan y no se delegan.
  .filter(([recurso]) => !RECURSOS_INTERNOS.has(recurso))
  .flatMap(([recurso, acciones]) =>
    (acciones as readonly string[]).map((accion) => {
      const codigo = `${recurso}.${accion}`;
      const meta = META[codigo];
      if (!meta) {
        // Fail-closed en desarrollo: un permiso nuevo en pfStatement sin su fila
        // en META llega a la consola sin etiqueta. Mejor romper el import que
        // mostrar un permiso anonimo en el editor de roles.
        throw new Error(`Falta metadata para el permiso "${codigo}" (agregarla en META)`);
      }
      return {
        codigo,
        recurso: recurso as Recurso,
        accion,
        tipo: meta.tipo,
        descripcion: meta.descripcion,
      };
    }),
  );

/** Set de codigos validos — para validar input que llega de la red. */
export const PERMISO_CODIGOS: ReadonlySet<string> = new Set(PERMISSIONS.map((p) => p.codigo));
